import { Fragment, useEffect, useState } from "react";
import { Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import swal from "sweetalert";
import api from "../../../api";
import PageTitle from "../../layouts/PageTitle";

const blankItem = () => ({
 itemName: "",
 warehouse: "",
 description: "",
 unit: "",
 quantity: "",
 rate: "",
 taxRate: 0,
 receiveHistory: [],
});

const emptyForm = {
 vendor: "",
 warehouse: "",
 warehouses: [],
 issueDate: "",
 expectedDeliveryDate: "",
 status: "Issued",
 remarks: "",
 termsAndConditions: "",
 items: [blankItem()],
};

const InventoryPurchaseOrderForm = () => {
 const { id } = useParams();
 const location = useLocation();
 const navigate = useNavigate();
 const isEdit = Boolean(id);
 const isViewMode = location.pathname.includes("/view/");
 const [loading, setLoading] = useState(isEdit);
 const [saving, setSaving] = useState(false);
 const [form, setForm] = useState(emptyForm);
 const [vendors, setVendors] = useState([]);
 const [warehouses, setWarehouses] = useState([]);
 const [units, setUnits] = useState([]);
 const isReadOnly = isViewMode;
 const warehouseOptions = warehouses.map((warehouse) => ({
 value: warehouse._id,
 label: `${warehouse.warehouseName} (${warehouse.warehouseCode})`,
 }));
 const selectedWarehouseOptions = warehouseOptions.filter((option) => form.warehouses.includes(option.value));
 const unitOptions = units.map((unit) => ({
 value: unit.symbol || unit.name,
 label: unit.symbol ? `${unit.name} (${unit.symbol})` : unit.name,
 }));

 useEffect(() => {
 const load = async () => {
 try {
 setLoading(true);
 const [vendorRes, warehouseRes] = await Promise.all([
 api.get("/vendors?limit=500"),
 api.get("/warehouses?limit=500"),
 ]);
 if (vendorRes.data.success) setVendors(vendorRes.data.data || []);
 if (warehouseRes.data.success) setWarehouses(warehouseRes.data.data || []);
 try {
 const unitRes = await api.get("/boq-unit-types");
 if (unitRes.data.success) setUnits(unitRes.data.data || []);
 } catch (unitError) {
 setUnits([]);
 }

 if (isEdit) {
 const poRes = await api.get(`/inventory-purchase-orders/${id}`);
 if (poRes.data.success) {
 const po = poRes.data.data;
 setForm({
 vendor: po.vendor?._id || po.vendor || "",
 warehouse: po.warehouse?._id || po.warehouse || "",
 warehouses: (Array.isArray(po.warehouses) && po.warehouses.length
 ? po.warehouses
 : [po.warehouse]).map((wh) => wh?._id || wh).filter(Boolean),
 issueDate: po.issueDate ? String(po.issueDate).slice(0, 10) : "",
 expectedDeliveryDate: po.expectedDeliveryDate ? String(po.expectedDeliveryDate).slice(0, 10) : "",
 status: po.status || "Draft",
 remarks: po.remarks || "",
 termsAndConditions: po.termsAndConditions || "",
 items: (po.items || []).length
 ? po.items.map((item) => ({
 lineId: item._id || "",
 itemName: item.itemName || "",
 warehouse: item.warehouse?._id || item.warehouse || po.warehouse || "",
 description: item.description || "",
 unit: item.unit || "",
 quantity: item.quantity ?? "",
 rate: item.rate ?? "",
 taxRate: item.taxRate ?? 0,
 receiveHistory: item.receiveHistory || [],
 }))
 : [blankItem()],
 });
 }
 }
 } catch (error) {
 swal("Error", "Failed to load inventory purchase order", "error");
 navigate("/inventory-purchase-orders");
 } finally {
 setLoading(false);
 }
 };

 load();
 }, [id, isEdit, navigate]);

 const handleChange = (e) => {
 const { name, value } = e.target;
 setForm((prev) => {
 if (name === "warehouse") {
 return {
 ...prev,
 warehouse: value,
 warehouses: value ? [value] : [],
 items: prev.items.map((item) => (item.warehouse ? item : { ...item, warehouse: value })),
 };
 }
 return { ...prev, [name]: value };
 });
 };

 const handleWarehousesChange = (selectedOptions) => {
 const selected = (selectedOptions || []).map((option) => option.value).filter(Boolean);
 setForm((prev) => {
 const nextWarehouse = selected[0] || "";
 return {
 ...prev,
 warehouses: selected,
 warehouse: nextWarehouse,
 items: prev.items.map((item) => {
 if (item.warehouse && selected.includes(item.warehouse)) {
 return item;
 }
 return { ...item, warehouse: nextWarehouse };
 }),
 };
 });
 };

 const handleItemChange = (index, name, value) => {
 setForm((prev) => {
 const items = [...prev.items];
 items[index] = { ...items[index], [name]: value };
 return { ...prev, items };
 });
 };

 const addItem = () => setForm((prev) => ({ ...prev, items: [...prev.items, { ...blankItem(), warehouse: prev.warehouses[0] || prev.warehouse || "" }] }));

 const removeItem = (index) => {
 setForm((prev) => {
 const items = prev.items.filter((_, i) => i !== index);
 return { ...prev, items: items.length ? items : [blankItem()] };
 });
 };

 const validate = () => {
 if (!form.vendor) return "Select vendor";
 if (!form.warehouses.length) return "Select at least one warehouse";
 if (!form.items.length) return "Add at least one item";
 for (const item of form.items) {
 if (!item.itemName?.trim()) return "Item name is required";
 if (!item.warehouse) return "Select warehouse for each item";
 if (!item.unit) return "Select unit for each item";
 if (!Number(item.quantity) || Number(item.quantity) <= 0) return "Item quantity must be greater than 0";
 if (Number(item.rate) < 0) return "Item rate cannot be negative";
 }
 return "";
 };

 const buildPayload = () => ({
 vendor: form.vendor,
 warehouse: form.warehouse,
 warehouses: form.warehouses,
 issueDate: form.issueDate || undefined,
 expectedDeliveryDate: form.expectedDeliveryDate || undefined,
 status: form.status,
 remarks: form.remarks,
 termsAndConditions: form.termsAndConditions,
 items: form.items.map((item) => ({
 lineId: item.lineId,
 itemName: item.itemName,
 warehouse: item.warehouse || form.warehouse,
 description: item.description,
 unit: item.unit,
 quantity: Number(item.quantity),
 rate: Number(item.rate),
 taxRate: Number(item.taxRate || 0),
 receiveHistory: Array.isArray(item.receiveHistory) ? item.receiveHistory : [],
 })),
 });

 const handleSubmit = async (e) => {
 e.preventDefault();
 const error = validate();
 if (error) return swal("Warning", error, "warning");

 try {
 setSaving(true);
 const payload = buildPayload();
 const res = isEdit
 ? await api.patch(`/inventory-purchase-orders/${id}`, payload)
 : await api.post("/inventory-purchase-orders", payload);

 if (res.data.success) {
 await swal("Success", `Inventory PO ${isEdit ? "updated" : "created"} successfully`, "success");
 navigate("/inventory-purchase-orders");
 }
 } catch (error) {
 swal("Error", error.response?.data?.message || "Failed to save inventory purchase order", "error");
 } finally {
 setSaving(false);
 }
 };

 const handleReceive = async () => {
 const rows = form.items.map((item) => ({
 lineId: item.lineId,
 itemName: item.itemName,
 warehouse: item.warehouse || form.warehouse,
 quantity: Number(item.quantity || 0),
 rate: Number(item.rate || 0),
 remarks: "",
 }));

 if (!rows.some((row) => row.quantity > 0)) {
 return swal("Warning", "Enter received quantity for at least one item", "warning");
 }

 try {
 setSaving(true);
 const res = await api.patch(`/inventory-purchase-orders/${id}/receive`, {
 items: rows,
 remarks: form.remarks,
 });
 if (res.data.success) {
 await swal("Success", "Stock received and updated", "success");
 navigate("/inventory-purchase-orders");
 }
 } catch (error) {
 swal("Error", error.response?.data?.message || "Failed to receive stock", "error");
 } finally {
 setSaving(false);
 }
 };

 if (loading) {
 return (
 <div className="text-center py-5">
 <Spinner animation="border" />
 </div>
 );
 }

 const totalAmount = form.items.reduce((sum, item) => {
 const qty = Number(item.quantity || 0);
 const rate = Number(item.rate || 0);
 const taxRate = Number(item.taxRate || 0);
 const amount = qty * rate;
 return sum + amount + amount * (taxRate / 100);
 }, 0);

 return (
 <Fragment>
 <PageTitle activeMenu={isViewMode ? "View Inventory PO" : isEdit ? "Edit Inventory PO" : "Add Inventory PO"} motherMenu="Inventory" />
 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center">
 <Card.Title className="mb-0">{isViewMode ? "View Inventory Purchase Order" : isEdit ? "Edit Inventory Purchase Order" : "Add Inventory Purchase Order"}</Card.Title>
 <Button variant="secondary" onClick={() => navigate("/inventory-purchase-orders")}>Back</Button>
 </Card.Header>
 <Card.Body>
 <Form onSubmit={handleSubmit}>
 <Row className="g-3">
 <Col md={6}>
 <Form.Label>Vendor *</Form.Label>
 <Form.Select name="vendor" value={form.vendor} onChange={handleChange} disabled={isReadOnly}>
 <option value="">Select Vendor</option>
 {vendors.map((vendor) => (
 <option key={vendor._id} value={vendor._id}>{vendor.name}</option>
 ))}
 </Form.Select>
 </Col>
 <Col md={6}>
 <Form.Label>Warehouses *</Form.Label>
 <Select
 isMulti
 isDisabled={isReadOnly}
 closeMenuOnSelect={false}
 placeholder="Select Warehouses"
 options={warehouseOptions}
 value={selectedWarehouseOptions}
 onChange={handleWarehousesChange}
 classNamePrefix="inventory-select"
 styles={{
 control: (base, state) => ({
 ...base,
 minHeight: "42px",
 borderColor: state.isFocused ? "#86b7fe" : "#ced4da",
 boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(13,110,253,.15)" : "none",
 borderRadius: "0.5rem",
 }),
 valueContainer: (base) => ({
 ...base,
 padding: "2px 10px",
 }),
 multiValue: (base) => ({
 ...base,
 backgroundColor: "#e7f3ff",
 borderRadius: "999px",
 }),
 multiValueLabel: (base) => ({
 ...base,
 color: "#0d6efd",
 fontWeight: 600,
 }),
 multiValueRemove: (base) => ({
 ...base,
 color: "#0d6efd",
 ":hover": {
 backgroundColor: "#dbeafe",
 color: "#084298",
 },
 }),
 }}
 />
 </Col>
 <Col md={3}>
 <Form.Label>Issue Date</Form.Label>
 <Form.Control type="date" name="issueDate" value={form.issueDate} onChange={handleChange} disabled={isReadOnly} />
 </Col>
 <Col md={3}>
 <Form.Label>Expected Delivery</Form.Label>
 <Form.Control type="date" name="expectedDeliveryDate" value={form.expectedDeliveryDate} onChange={handleChange} disabled={isReadOnly} />
 </Col>
 <Col md={3}>
 <Form.Label>Status</Form.Label>
 <Form.Select name="status" value={form.status} onChange={handleChange} disabled={isReadOnly}>
 <option value="Draft">Draft</option>
 <option value="Issued">Issued</option>
 <option value="Partially Received">Partially Received</option>
 <option value="Received">Received</option>
 <option value="Cancelled">Cancelled</option>
 </Form.Select>
 
 </Col>
 <Col md={3}>
 <Form.Label>Total</Form.Label>
 <Form.Control value={`Rs. ${totalAmount.toFixed(2)}`} disabled />
 </Col>
 </Row>

 <div className="mt-4">
 <div className="d-flex justify-content-between align-items-center mb-2">
 <h6 className="mb-0">Items</h6>
 {!isReadOnly && (
 <Button type="button" variant="outline-primary" size="sm" onClick={addItem}>+ Add Item</Button>
 )}
 </div>
 <div className="table-responsive">
 <Table bordered style={{ minWidth: "1600px" }}>
 <thead>
 <tr>
 <th style={{ minWidth: 220 }}>Item Name</th>
 <th style={{ minWidth: 220 }}>Warehouse</th>
 <th style={{ minWidth: 260 }}>Description</th>
 <th style={{ minWidth: 170 }}>Unit</th>
 <th style={{ minWidth: 140 }}>Quantity</th>
 <th style={{ minWidth: 150 }}>Rate</th>
 <th style={{ minWidth: 130 }}>Tax %</th>
 <th style={{ minWidth: 130 }}>Actions</th>
 </tr>
 </thead>
 <tbody>
 {form.items.map((item, index) => (
 <tr key={index}>
 <td><Form.Control value={item.itemName} onChange={(e) => handleItemChange(index, "itemName", e.target.value)} disabled={isReadOnly} /></td>
 <td>
 <Form.Select value={item.warehouse || ""} onChange={(e) => handleItemChange(index, "warehouse", e.target.value)} disabled={isReadOnly}>
 <option value="">Select Warehouse</option>
 {(form.warehouses.length
 ? warehouses.filter((warehouse) => form.warehouses.includes(warehouse._id))
 : warehouses
 ).map((warehouse) => (
 <option key={warehouse._id} value={warehouse._id}>
 {warehouse.warehouseName}
 </option>
 ))}
 </Form.Select>
 </td>
 <td><Form.Control value={item.description} onChange={(e) => handleItemChange(index, "description", e.target.value)} disabled={isReadOnly} /></td>
 <td>
 <Form.Select value={item.unit || ""} onChange={(e) => handleItemChange(index, "unit", e.target.value)} disabled={isReadOnly}>
 <option value="">Select Unit</option>
 {(item.unit && !unitOptions.some((option) => option.value === item.unit)
 ? [{ value: item.unit, label: item.unit }, ...unitOptions]
 : unitOptions
 ).map((unit) => (
 <option key={unit.value} value={unit.value}>
 {unit.label}
 </option>
 ))}
 </Form.Select>
 </td>
 <td>
 <Form.Control type="number" min="0" style={{ minWidth: 140 }} value={item.quantity} onChange={(e) => handleItemChange(index, "quantity", e.target.value)} disabled={isReadOnly} />
 </td>
 <td><Form.Control type="number" min="0" style={{ minWidth: 130 }} value={item.rate} onChange={(e) => handleItemChange(index, "rate", e.target.value)} disabled={isReadOnly} /></td>
 <td><Form.Control type="number" min="0" style={{ minWidth: 110 }} value={item.taxRate} onChange={(e) => handleItemChange(index, "taxRate", e.target.value)} disabled={isReadOnly} /></td>
 <td>
 {!isReadOnly && (
 <Button type="button" size="sm" variant="outline-danger" onClick={() => removeItem(index)}>
 Remove
 </Button>
 )}
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 </div>
 </div>

 {isEdit && form.items.some((item) => Array.isArray(item.receiveHistory) && item.receiveHistory.length > 0) && (
 <div className="mt-4">
 <h6 className="mb-2">Receive History</h6>
 <div className="table-responsive">
 <Table bordered size="sm">
 <thead>
 <tr>
 <th>Item</th>
 <th>Qty</th>
 <th>Rate</th>
 <th>Received At</th>
 <th>Remarks</th>
 </tr>
 </thead>
 <tbody>
 {form.items.flatMap((item) =>
 (item.receiveHistory || []).map((entry, idx) => (
 <tr key={`${item.itemName}-${idx}`}>
 <td>{item.itemName}</td>
 <td>{Number(entry.quantity || 0)}</td>
 <td>Rs. {Number(entry.rate || 0).toFixed(2)}</td>
 <td>{entry.receivedAt ? new Date(entry.receivedAt).toLocaleString() : "-"}</td>
 <td>{entry.remarks || "-"}</td>
 </tr>
 ))
 )}
 </tbody>
 </Table>
 </div>
 </div>
 )}

 <Row className="g-3 mt-2">
 <Col md={6}>
 <Form.Label>Remarks</Form.Label>
 <Form.Control as="textarea" rows={3} name="remarks" value={form.remarks} onChange={handleChange} disabled={isReadOnly} />
 </Col>
 <Col md={6}>
 <Form.Label>Terms and Conditions</Form.Label>
 <Form.Control as="textarea" rows={3} name="termsAndConditions" value={form.termsAndConditions} onChange={handleChange} disabled={isReadOnly} />
 </Col>
 </Row>

 <div className="d-flex justify-content-end gap-2 mt-4">
 <Button type="button" variant="light" onClick={() => navigate("/inventory-purchase-orders")}>Cancel</Button>
 {isViewMode ? null : isEdit && form.status !== "Received" && (
 <Button type="button" variant="warning" onClick={handleReceive} disabled={saving}>
 Receive Stock
 </Button>
 )}
 {!isReadOnly && !isViewMode && (
 <Button type="submit" disabled={saving}>
 {saving ? "Saving..." : "Save PO"}
 </Button>
 )}
 </div>
 {isViewMode ? (
 <div className="alert alert-info mt-3 mb-0">
 View mode enabled. This purchase order cannot be edited from here.
 </div>
 ) : null}
 </Form>
 </Card.Body>
 </Card>
 </Col>
 </Row>
 </Fragment>
 );
};

export default InventoryPurchaseOrderForm;
